import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { caseQuickUpdate } from '../../../../lib/fetchApi'
import { useUserTabEditPermissionDisabled } from '../../../../lib/usersPermissions'
import { caseActions } from '../../../../store/case'
import { notificationActions } from '../../../../store/notifications'
import CheckboxCell from '../../../common/interactive/CheckboxCell'
import BodySimpleModalV3 from '../../../common/modal/BodySimpleModalV3'
import { useDoNotLienConfirm } from './doNotLienConfirm'

export { useDoNotLienConfirm, DoNotLienConfirmModal } from './doNotLienConfirm'

export default function DoNotLienToggle({ activeCase, inWorkQ }) {
    const dispatch = useDispatch()
    const userToken = useSelector(state => state.user.token)
    const caseId = activeCase?.case?.id
    const checked = Boolean(activeCase?.case?.do_not_lien)
    const permissionDisabled = useUserTabEditPermissionDisabled('lien_do_not_lien')
    const [saving, setSaving] = useState(false)
    // Bumping the key remounts the shared CheckboxCell so its internal optimistic
    // state resets back to the redux `checked` value (used when the confirm is cancelled).
    const [cellKey, setCellKey] = useState(0)
    const { confirm: confirmDoNotLien, modal: confirmModal } = useDoNotLienConfirm()

    function resetCell() {
        setCellKey(k => k + 1)
    }

    function applyToStore(nextValue, audit = {}) {
        const fields = [{ field: 'do_not_lien', value: nextValue }]
        if (Object.prototype.hasOwnProperty.call(audit, 'do_not_lien_at')) {
            fields.push({ field: 'do_not_lien_at', value: audit.do_not_lien_at })
        }
        if (Object.prototype.hasOwnProperty.call(audit, 'do_not_lien_by_id')) {
            fields.push({ field: 'do_not_lien_by_id', value: audit.do_not_lien_by_id })
        }
        dispatch(caseActions.modifyActiveCaseMultipleObject({
            folder: 'case',
            item: { fields },
        }))
    }

    async function toggleHandler(payload) {
        if (!caseId) {
            return
        }

        const nextValue = payload?.data?.value ? 1 : 0
        if (nextValue === (checked ? 1 : 0)) {
            return
        }

        const engaging = nextValue === 1
        const confirmed = await confirmDoNotLien({ engaging })
        if (!confirmed) {
            resetCell()
            return
        }

        setSaving(true)
        const body = {
            type: 'case',
            fields: { do_not_lien: nextValue },
        }
        if (inWorkQ) {
            body.workq = inWorkQ
        }

        const update = await caseQuickUpdate(userToken, caseId, body, null, inWorkQ || false)

        if (update?.staleResponse) {
            setSaving(false)
            return
        }

        if (update?.errorMessage) {
            dispatch(notificationActions.setError(update))
            resetCell()
            setSaving(false)
            return
        }

        applyToStore(nextValue, update?.caseData?.case ?? {})
        setSaving(false)
    }

    const tooltip = permissionDisabled
        ? "You don't have permission to toggle Do Not Lien"
        : saving
          ? 'Saving…'
          : null

    return (
        <>
            <BodySimpleModalV3 message={tooltip} disabled={!tooltip} widthAuto>
                <CheckboxCell
                    key={cellKey}
                    label="Do Not Lien"
                    name="do_not_lien"
                    checked={checked}
                    isDisabled={permissionDisabled || saving}
                    action={toggleHandler}
                    identifier={{ type: 'case', field: 'do_not_lien', permissionType: 'lien_do_not_lien' }}
                />
            </BodySimpleModalV3>
            {confirmModal}
        </>
    )
}
